import { apiClient } from '@/core/infra_api_client'
import type {
  CloudEnvironment,
  CreateEnvironmentRequest,
  UpdateEnvironmentRequest,
  PaginatedResponse,
} from '@/core/infra_types'

const BASE_PATH = 'environments'

export async function listEnvironments(params?: {
  page?: number
  perPage?: number
  status?: string
  cloudProvider?: string
}): Promise<PaginatedResponse<CloudEnvironment>> {
  const searchParams: Record<string, string | number | boolean> = {}
  if (params?.page) searchParams['page'] = params.page
  if (params?.perPage) searchParams['per_page'] = params.perPage
  if (params?.status) searchParams['status'] = params.status
  if (params?.cloudProvider) searchParams['cloud_provider'] = params.cloudProvider

  return apiClient.get<PaginatedResponse<CloudEnvironment>>(BASE_PATH, searchParams)
}

export async function getEnvironment(id: string): Promise<CloudEnvironment> {
  return apiClient.get<CloudEnvironment>(`${BASE_PATH}/${id}`)
}

export async function createEnvironment(data: CreateEnvironmentRequest): Promise<CloudEnvironment> {
  return apiClient.post<CloudEnvironment, CreateEnvironmentRequest>(BASE_PATH, data)
}

export async function updateEnvironment(
  id: string,
  data: UpdateEnvironmentRequest
): Promise<CloudEnvironment> {
  return apiClient.patch<CloudEnvironment, UpdateEnvironmentRequest>(`${BASE_PATH}/${id}`, data)
}

export async function deleteEnvironment(id: string): Promise<void> {
  return apiClient.delete(`${BASE_PATH}/${id}`)
}

/**
 * Update the environment's config JSONB via PUT.
 *
 * NOTE: This uses apiClient.put, NOT apiClient.patch. The backend router mounts
 * PUT only (`r.Put("/", envHandler.Update)` in infra_router.go). The existing
 * updateEnvironment() method uses apiClient.patch and is latent-broken — fixing
 * that is tracked as a separate cleanup task.
 *
 * Callers are responsible for sending the FULL merged config (backend's
 * EnvironmentService.Update replaces env.Config wholesale). Use
 * writeHelmOverrides from helm_overrides_merge.ts to build the payload.
 */
export async function updateEnvironmentConfig(
  id: string,
  config: Record<string, unknown>
): Promise<CloudEnvironment> {
  return apiClient.put<CloudEnvironment, { config: Record<string, unknown> }>(
    `${BASE_PATH}/${id}`,
    { config }
  )
}
